import * as React from "react"

import { EmptyState, Skeleton } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { usePledgesQuery } from "@/features/pledges/services/pledges-query"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import { ProgressBar } from "@/features/finance/overview/components/progress-bar"
import {
  useFinanceOverviewPeriod,
  useFinanceOverviewScope,
} from "@/features/finance/overview/context/finance-overview-context"
import { formatMoneySafe, formatPercent } from "@/features/finance/overview/lib/overview-format"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"
import { type FinanceSurfaceContext } from "@/features/finance/hooks/finance-context-rules"
import { WidgetShareNote } from "@/features/finance/overview/widgets/widget-share-note"
import {
  useWidgetCreateActions,
  type WidgetCreateKey,
} from "@/features/finance/overview/lib/use-widget-create-actions"

const WIDGET_CREATE_KEYS: readonly WidgetCreateKey[] = ["pledge"]

interface PledgeData {
  /** Canonical decimal money strings, summed as decimals on the server (IBA-116). */
  totalPledged: string
  totalReceived: string
  totalRemaining: string
  /** The currency those three are in, as the server stated it — never a hardcoded code. */
  currency: string
  count: number
  /** A RATIO, served by the API and clamped to 0–100 there. */
  fulfillment: number
}

/**
 * Pledge Summary — a concise, church-specific pledge summary for the selected period: total pledged,
 * fulfilled, outstanding, a fulfillment progress bar, and the active pledge count. Period-sensitive
 * (pledgedAt range). Pledges are branch-only, so in parish context we show an honest unavailable state
 * rather than fake zeros.
 */
export function PledgeSummaryWidget({ context }: { context?: FinanceSurfaceContext } = {}) {
  const t = useTranslations("finance.overview")
  const scope = useFinanceOverviewScope()
  const period = useFinanceOverviewPeriod()
  const go = useOverviewNav()

  const isParish = scope.context === "parish"

  const query = usePledgesQuery(
    scope.workspaceId && scope.canView && !isParish
      ? {
          workspaceId: scope.workspaceId,
          branchId: scope.branchId ?? undefined,
          pledgedAtFrom: period.from,
          pledgedAtTo: period.to,
          limit: 1,
        }
      : null,
  )

  const data = React.useMemo<PledgeData | undefined>(() => {
    const summary = query.data?.summary
    if (!summary) return undefined
    // Every figure is read, not computed. Dividing two money strings in a browser to fill a progress bar is
    // exactly the arithmetic IBA-116 removes — the API serves the ratio, already clamped.
    return {
      totalPledged: summary.totalPledged,
      totalReceived: summary.totalReceived,
      totalRemaining: summary.totalRemaining,
      currency: summary.currency,
      count: summary.count,
      fulfillment: summary.fulfillmentPercentage,
    }
  }, [query.data])

  const create = useWidgetCreateActions(WIDGET_CREATE_KEYS)
  useWidgetMenu({
    refresh: () => void query.refetch(),
    viewDetails: go.canNavigateTo(OverviewRoutes.pledges) ? () => go(OverviewRoutes.pledges) : undefined,
    create,
  })

  if (isParish) {
    return <EmptyState surface={false} compact title={t("widgets.pledgeSummary.unavailable")} />
  }

  return (
    <WidgetBoundary
      isLoading={query.isLoading}
      isError={query.isError}
      isFetching={query.isFetching}
      data={data}
      isEmpty={(d) => d.count === 0}
      skeleton={<PledgeSkeleton />}
      errorTitle={t("widgets.pledgeSummary.error")}
      onRetry={() => void query.refetch()}
      retryLabel={t("widgets.common.retry")}
      emptyTitle={t("widgets.pledgeSummary.empty.title")}
    >
      {(d) => (
        <div className="space-y-3">
          <div className="space-y-1.5 text-sm">
            <Row
              label={t("widgets.pledgeSummary.pledged")}
              value={formatMoneySafe(d.totalPledged, d.currency || scope.currency)}
            />
            <Row
              label={t("widgets.pledgeSummary.fulfilled")}
              value={formatMoneySafe(d.totalReceived, d.currency || scope.currency)}
            />
            <Row
              label={t("widgets.pledgeSummary.outstanding")}
              value={formatMoneySafe(d.totalRemaining, d.currency || scope.currency)}
            />
          </div>
          {/* Under the figures it describes — a caption above them reads as a heading. */}
          <WidgetShareNote context={context} />
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{t("widgets.pledgeSummary.fulfillment")}</span>
              <span className="font-medium tabular-nums">{formatPercent(d.fulfillment)}</span>
            </div>
            <ProgressBar value={d.fulfillment} tone="success" aria-label={t("widgets.pledgeSummary.fulfillment")} />
          </div>
          <p className="text-xs text-muted-foreground">
            {t("widgets.pledgeSummary.activeCount", { count: d.count })}
          </p>
        </div>
      )}
    </WidgetBoundary>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  )
}

function PledgeSkeleton() {
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center justify-between">
            <Skeleton className="h-3.5 w-24" />
            <Skeleton className="h-3.5 w-16" />
          </div>
        ))}
      </div>
      <Skeleton className="h-1.5 w-full" />
      <Skeleton className="h-3 w-28" />
    </div>
  )
}
